import React, { useEffect, useMemo, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import { getAccessPassEvents, updateAccessPass } from '../../utilities/api'

function groupEventsBySeason(events) {
  const groups = new Map()

  for (const eventItem of events) {
    const seasonKey = eventItem.season?.uuid || 'no-season'
    const existing = groups.get(seasonKey)
    if (existing) {
      existing.events.push(eventItem)
    } else {
      groups.set(seasonKey, {
        key: seasonKey,
        name: eventItem.season?.name || 'No season',
        start: eventItem.season?.start || null,
        events: [eventItem],
      })
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'no-season') return 1
    if (b.key === 'no-season') return -1
    if (a.start && b.start) {
      return new Date(b.start) - new Date(a.start)
    }
    return a.name.localeCompare(b.name)
  })
}

export function EditAccessPassModal({
  show,
  setShow,
  organizationUUID,
  pass,
  onSaved,
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('active')
  const [events, setEvents] = useState([])
  const [selectedEventUUIDs, setSelectedEventUUIDs] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const isOrganizerPass = pass?.type === 'organizer'

  useEffect(() => {
    if (!show || !pass) return

    setName(pass.name || '')
    setEmail(pass.users_permissions_user?.email || pass.email || '')
    setStatus(pass.status || 'active')
    setSelectedEventUUIDs(
      (pass.events || []).map((eventItem) => eventItem.uuid)
    )
    setError('')

    if (!isOrganizerPass) return

    setLoadingEvents(true)
    getAccessPassEvents(organizationUUID)
      .then((res) => {
        setEvents(res?.data?.data || [])
      })
      .catch((err) => {
        console.error(err)
        setError('Could not load events for this organization.')
      })
      .finally(() => {
        setLoadingEvents(false)
      })
  }, [show, pass, organizationUUID, isOrganizerPass])

  const eventsBySeason = useMemo(
    () => groupEventsBySeason(isOrganizerPass ? events : pass?.events || []),
    [events, isOrganizerPass, pass?.events]
  )

  const toggleEvent = (uuid) => {
    setSelectedEventUUIDs((current) =>
      current.includes(uuid)
        ? current.filter((id) => id !== uuid)
        : [...current, uuid]
    )
  }

  const canSubmit =
    name && (isOrganizerPass ? selectedEventUUIDs.length > 0 : true)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const payload = {
        passId: pass.uuid || pass.id,
        organizationUUID,
        name,
        email: email || null,
        status,
      }

      if (isOrganizerPass) {
        payload.eventUUIDs = selectedEventUUIDs
      }

      await updateAccessPass(payload)
      setShow(false)
      onSaved?.()
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Failed to update pass')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      fullscreen
      scrollable
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit access pass</Modal.Title>
      </Modal.Header>
      <Form
        onSubmit={handleSubmit}
        className="d-flex flex-column flex-grow-1 overflow-hidden"
      >
        <Modal.Body>
          <Stack gap={3}>
            <p className="small text-muted mb-0">
              Code: {pass?.checkInCode}
              {pass?.type === 'season_seat'
                ? ' · Season seat pass'
                : ' · Organizer pass'}
            </p>
            <Form.Group controlId="editAccessPassName">
              <Form.Label>Pass name</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="editAccessPassEmail">
              <Form.Label>Assign to email (optional)</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="editAccessPassStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="revoked">Revoked</option>
              </Form.Select>
            </Form.Group>
            {isOrganizerPass ? (
              <div>
                <Form.Label>Events</Form.Label>
                {loadingEvents ? (
                  <p className="small text-muted mb-0 mt-2">
                    Loading events...
                  </p>
                ) : events.length === 0 ? (
                  <p className="small text-muted mb-0 mt-2">
                    No eligible events found for this organization.
                  </p>
                ) : (
                  <Stack gap={3} className="mt-2">
                    {eventsBySeason.map((seasonGroup) => (
                      <div key={seasonGroup.key}>
                        <p className="small fw-semibold text-muted text-uppercase mb-2">
                          {seasonGroup.name}
                        </p>
                        <Stack gap={2}>
                          {seasonGroup.events.map((eventItem) => (
                            <Form.Check
                              key={eventItem.uuid}
                              type="checkbox"
                              id={`edit-access-pass-event-${eventItem.uuid}`}
                              className="mb-0"
                              style={{ whiteSpace: 'normal' }}
                              label={`${eventItem.name}${eventItem.status ? ` (${eventItem.status})` : ''}`}
                              checked={selectedEventUUIDs.includes(
                                eventItem.uuid
                              )}
                              onChange={() => toggleEvent(eventItem.uuid)}
                            />
                          ))}
                        </Stack>
                      </div>
                    ))}
                  </Stack>
                )}
              </div>
            ) : (
              <div>
                <Form.Label>Events</Form.Label>
                <Stack gap={3} className="mt-2">
                  {eventsBySeason.map((seasonGroup) => (
                    <div key={seasonGroup.key}>
                      <p className="small fw-semibold text-muted text-uppercase mb-2">
                        {seasonGroup.name}
                      </p>
                      <Stack gap={1}>
                        {seasonGroup.events.map((eventItem) => (
                          <p className="small mb-0" key={eventItem.uuid}>
                            {eventItem.name}
                            {eventItem.status ? ` (${eventItem.status})` : ''}
                          </p>
                        ))}
                      </Stack>
                    </div>
                  ))}
                </Stack>
                <p className="small text-muted mb-0 mt-2">
                  Season seat events are managed by the package and cannot be
                  edited here.
                </p>
              </div>
            )}
            {error && <p className="text-danger mb-0">{error}</p>}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || !canSubmit}>
            {submitting ? 'Saving...' : 'Save pass'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
